import { createApp } from "vue";
import App from "./App.vue";
import { createGtm } from "@gtm-support/vue-gtm";

/*
TIP: To get started with clean router change path to @/router/clean.ts.
 */
import router from "./router";
import store from "./store";
import ElementPlus from "element-plus";
import i18n from "@/core/plugins/i18n";

//imports for app initialization
import ApiService from "@/core/services/ApiService";
import { initApexCharts } from "@/core/plugins/apexcharts";
import { initInlineSvg } from "@/core/plugins/inline-svg";
import { initVeeValidate } from "@/core/plugins/vee-validate";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";

import "@/core/plugins/prismjs";
import { Actions } from "./store/enums/StoreEnums";
import { messaging, onMessage } from "@/firebase/init";
// import NotificationPermissionChecker from "@/core/services/NotificationPermissionChecker";
// import NotificationService from "@/core/services/NotificationService";
// import NotificationErrorHandler from "@/core/services/NotificationErrorHandler";
const app = createApp(App);

app.use(store);
app.use(router);
app.use(ElementPlus);

app.component("QuillEditor", QuillEditor);

ApiService.init(app);
initApexCharts(app);
initInlineSvg(app);
initVeeValidate();

// add interceptor for get refresh token.
ApiService.vueInstance.axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // if access token is expired!.
    const originalConfig = error.config;

    // if get error with response.
    if (error.response) {
      // if get unauthorzied.
      if (error.response.status == 401) {
        if (!originalConfig._retry) {
          originalConfig._retry = true;

          try {
            const response = await store.dispatch(Actions.REFRESH_TOKEN);
            originalConfig.headers["Authorization"] =
              response.AuthenticationResult.AccessToken;
            originalConfig.baseURL = undefined;

            return ApiService.vueInstance.axios(originalConfig);
          } catch (err) {
            const currentRoute = router.currentRoute.value;
            store.dispatch(
              Actions.PURGE_AUTH,
              !currentRoute.meta || !currentRoute.meta.requiresAuth // if current route without auth.
                ? {
                    route: router.currentRoute.value,
                  }
                : {}
            );
            return Promise.reject(err);
          }
        } else {
          const currentRoute = router.currentRoute.value;
          store.dispatch(
            Actions.PURGE_AUTH,
            !currentRoute.meta || !currentRoute.meta.requiresAuth // if current route without auth.
              ? {
                  route: router.currentRoute.value,
                }
              : {}
          );
          router.push({ name: "SignIn" });
          return Promise.reject(error);
        }
      }
    } else {
      if (!originalConfig._retry) {
        originalConfig._retry = true;

        try {
          const response = await store.dispatch(Actions.REFRESH_TOKEN);
          originalConfig.headers["Authorization"] =
            response.AuthenticationResult.AccessToken;
          originalConfig.baseURL = undefined;

          return ApiService.vueInstance.axios(originalConfig);
        } catch (err) {
          const currentRoute = router.currentRoute.value;
          store.dispatch(
            Actions.PURGE_AUTH,
            !currentRoute.meta || !currentRoute.meta.requiresAuth // if current route without auth.
              ? {
                  route: router.currentRoute.value,
                }
              : {}
          );
          return Promise.reject(err);
        }
      }
    }

    // store.dispatch(Actions.PURGE_AUTH);
    return Promise.reject(error);
  }
);

onMessage(messaging, (payload: any) => {
  if (document.visibilityState !== "visible") {
    console.log("Skipping foreground notification because tab is not visible");
    return;
  }
  console.log("forground payload", payload);

  // // Check if notifications are still permitted
  // if (Notification.permission !== "granted") {
  //   console.warn("Notification permission revoked, cannot show notification");
  //   const notificationService = NotificationService.getInstance();
  //   notificationService.showPermissionRevokedMessage();
  //   return;
  // }

  const notificationTitle = payload.data?.title;
  const notificationOptions = {
    body: payload.data?.body,
    icon: "/favicon/apple-touch-icon-57x57-precomposed.png",
    badge: "/favicon/apple-touch-icon-precomposed.png",
  };

  try {
    const notification = new Notification(
      notificationTitle as "",
      notificationOptions
    );

    notification.onclick = (evt) => {
      let url = window.location.origin + "/dashboard";
      const target = JSON.parse(payload.data.target);

      switch (target.type) {
        case "complete_job": {
          url += `/jobs/${target.job_id}`;
          break;
        }

        case "cancel_job": {
          url += `/jobs/${target.job_id}`;
          break;
        }

        case "submit_offer": {
          url += `/jobs/${target.job_id}/offers/${target.offer_id}`;
          break;
        }

        case "accept_offer": {
          url += `/jobs/${target.job_id}/offers/${target.offer_id}`;
          break;
        }

        case "reject_offer": {
          url += `/jobs/${target.job_id}/offers/${target.offer_id}`;
          break;
        }

        case "cancel_offer": {
          url += `/jobs/${target.job_id}/offers/${target.offer_id}`;
          break;
        }

        case "chat": {
          url += `/jobs/${target.chat_id.split("_")[0]}/messages?collectionId=${
            target.chat_id
          }`;
          break;
        }

        case "payment": {
          url += `/payments/${target.payment_id}`;
          break;
        }

        case "document": {
          url += `/jobs/${target.job_id}/home?documentId=${target.document_id}`;
          break;
        }

        case "purchase_product": {
          url += `/jobs/${target.job_id}/home`;
          break;
        }

        default:
          break;
      }

      console.log("notification clicked", url);

      window.open(url, "_blank");
      setTimeout(() => {
        notification.close();
      }, 300);
    };
  } catch (error) {
    console.error("Failed to show notification:", error);
    // const errorHandler = NotificationErrorHandler.getInstance();
    // errorHandler.handleFirebaseError(error);
  }
});

app.use(i18n);

app.use(
  createGtm({
    id: process.env.VUE_APP_GTM_ID || "", // or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
    // queryParams: {
    //   // Add URL query string when loading gtm.js with GTM ID (required when using custom environments)
    //   gtm_auth: "AB7cDEf3GHIjkl-MnOP8qr",
    //   gtm_preview: "env-4",
    //   gtm_cookies_win: "x",
    // },
    // source: "https://customurl.com/gtm.js", // Add your own serverside GTM script
    defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
    compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
    nonce: "e26f481a37", // Will add `nonce` to the script tag
    enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: false, // Whether or not display console logs debugs (optional)
    loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
    ignoredViews: [], // Don't trigger events for specified router names (optional)
    trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
  })
);

app.mount("#app");

// // Initialize notification permission checker
// const permissionChecker = NotificationPermissionChecker.getInstance();
// permissionChecker.initialize();

// // Reinitialize notifications on route change (for post-login scenarios)
// router.afterEach(() => {
//   setTimeout(() => {
//     if (store.getters.isUserAuthenticated) {
//       permissionChecker.initialize();
//     }
//   }, 1000);
// });

if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then((registration) => {
      console.log(
        "✅ Service Worker registered with scope:",
        registration.scope
      );
    })
    .catch((error) => {
      console.error("❌ Service Worker registration failed:", error);
      // const errorHandler = NotificationErrorHandler.getInstance();
      // errorHandler.handleServiceWorkerError(error);
    });
}
